import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import { ModalImportar } from "../components/BotonImportarRutasExcel";
import "../styles/rutas-impresion.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _hoy_legible() {
  const d = new Date();
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
}

async function descargarBlob(path, defaultName) {
  try {
    const res = await axios.get(`${API}${path}`, { responseType: "blob" });
    // Tomar filename del header si viene, sino default
    const cd = res.headers["content-disposition"] || "";
    const m = /filename="?([^"]+)"?/.exec(cd);
    const filename = m ? m[1] : defaultName;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Descargado: ${filename}`);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Error al descargar el archivo");
  }
}

export default function RutasImpresionPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openImportar, setOpenImportar] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/rutas-impresion`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar las rutas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleImprimir = () => window.print();
  const esAdmin = user?.rol === "admin";

  return (
    <div className="hub-bg ri-root" data-testid="page-rutas-impresion">
      {/* ── BARRA SUPERIOR (no se imprime) ───────────────────── */}
      <header className="topnav ri-no-print">
        <div className="brand">
          <button onClick={() => navigate("/hub")} className="iconbtn mr-1" aria-label="Atrás" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left"/>
          </button>
          <div className="logo" style={{ background: "#1D4ED8" }}>
            <i className="fa-solid fa-print"/>
          </div>
          <div>
            <div className="title">Rutas por zona · Impresión</div>
            <div className="sub">DMU 614 · {data?.total_zonas || 0} zonas · {data?.total_espacios || 0} espacios</div>
          </div>
        </div>
        <div className="actions">
          <div className="hidden sm:block text-right mr-2">
            <div className="text-sm font-semibold leading-tight">{user?.nombre}</div>
            <div className="text-xs text-slate-500">{user?.rol}</div>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="btn-logout-ri" style={{ padding: "0.5rem 0.9rem" }}>
            <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 ri-main">
        {/* ── BOTONES (no se imprimen) ─────────────────────── */}
        <div className="ri-toolbar ri-no-print" data-testid="ri-toolbar">
          <button
            className="btn-primary"
            onClick={handleImprimir}
            disabled={loading || !data}
            data-testid="btn-imprimir-todo"
          >
            <i className="fa-solid fa-print mr-2"/>Imprimir todo
          </button>
          <button
            className="btn-ghost"
            onClick={() => descargarBlob("/admin/exportar/espacios.xlsx", "espacios_publicos.xlsx")}
            disabled={loading || !esAdmin}
            title={esAdmin ? "Descargar Excel" : "Solo admin"}
            data-testid="btn-exportar-excel-rutas"
          >
            <i className="fa-solid fa-file-excel mr-2"/>Exportar Excel
          </button>
          {esAdmin && (
            <button
              className="btn-ghost"
              onClick={() => setOpenImportar(true)}
              data-testid="btn-importar-rutas-ri"
              title="Importar pipas_editado.xlsx"
              style={{ background: "#10B981", color: "white", borderColor: "#059669" }}
            >
              <i className="fa-solid fa-upload mr-2"/>Importar rutas
            </button>
          )}
          <button
            className="btn-ghost"
            onClick={cargar}
            disabled={loading}
            data-testid="btn-refresh-ri"
          >
            <i className={`fa-solid fa-rotate ${loading ? "fa-spin" : ""} mr-2`}/>Actualizar
          </button>
        </div>

        {/* ── ENCABEZADO IMPRIMIBLE ────────────────────────── */}
        <div className="ri-print-header" data-testid="ri-print-header">
          <div className="ri-print-logo">
            <i className="fa-solid fa-truck-droplet"/>
          </div>
          <div className="ri-print-titles">
            <div className="ri-print-title">DMU 614 · Rutas por zona</div>
            <div className="ri-print-sub">
              Generado el {_hoy_legible()}
              {user?.nombre ? <> · Impreso por <strong>{user.nombre}</strong></> : null}
              <span> · {data?.total_zonas || 0} zonas · {data?.total_espacios || 0} espacios</span>
            </div>
          </div>
        </div>

        {/* ── LISTA DE ZONAS ──────────────────────────────── */}
        {loading && !data && (
          <div className="ri-empty">Cargando rutas…</div>
        )}

        {data && data.zonas.map((z) => (
          <section key={z.zona} className="ri-zona" data-testid={`ri-zona-${z.zona}`}>
            <header className="ri-zona-head">
              <div className="ri-zona-badge" data-testid={`ri-zona-badge-${z.zona}`}>{z.zona}</div>
              <div className="ri-zona-meta">
                <div className="ri-zona-line">
                  <strong>{z.zona}</strong>
                  <span className="ri-sep">·</span>
                  <span>Pipa <strong>#{z.pipa_id || "—"}</strong></span>
                  <span className="ri-sep">·</span>
                  <span>{z.chofer_nombre || "Sin chofer titular"}</span>
                  <span className="ri-sep">·</span>
                  <span><strong>{z.total}</strong> parques</span>
                </div>
              </div>
            </header>

            {z.espacios.length === 0 ? (
              <div className="ri-zona-vacia">Sin espacios registrados.</div>
            ) : (
              <ol className="ri-lista">
                {z.espacios.map((e) => (
                  <li key={`${z.zona}-${e.clave_ruta}-${e.numero}`} className="ri-item" data-testid={`ri-item-${z.zona}-${e.numero}`}>
                    <span className="ri-num">{e.numero}.</span>
                    <span className="ri-nombre">{e.nombre}</span>
                    <span className="ri-sep">·</span>
                    <span className="ri-clave mono">{e.clave_ruta}</span>
                    <span className="ri-sep">·</span>
                    <span className="ri-tipo">{e.tipo}</span>
                    <span className="ri-sep">·</span>
                    <span className="ri-colonia">{e.colonia}</span>
                  </li>
                ))}
              </ol>
            )}

            <footer className="ri-zona-foot">Total: <strong>{z.total}</strong> espacios</footer>
          </section>
        ))}

        {/* ── EXPORTAR BASE DE DATOS (SOLO ADMIN) ─────────── */}
        {esAdmin && (
          <section className="card-base ri-exportar ri-no-print" data-testid="seccion-exportar-bd">
            <h2 className="text-lg font-bold m-0 mb-1">
              <i className="fa-solid fa-database mr-2 text-blue-700"/>Exportar base de datos
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              Descarga los datos principales del sistema en formato Excel.
              <strong className="text-amber-700"> Estos archivos contienen información sensible: trátalos con cuidado.</strong>
            </p>

            <div className="ri-export-grid">
              <button
                className="ri-export-card"
                onClick={() => descargarBlob("/admin/exportar/espacios.xlsx", "espacios_publicos.xlsx")}
                data-testid="btn-exportar-espacios"
              >
                <div className="ri-export-icon" style={{ background: "#10B981" }}>
                  <i className="fa-solid fa-tree"/>
                </div>
                <div className="ri-export-info">
                  <div className="ri-export-title">Espacios públicos (Excel)</div>
                  <div className="ri-export-sub">Zonas Z1-Z20 en hojas separadas</div>
                </div>
                <i className="fa-solid fa-download ri-export-arrow"/>
              </button>

              <button
                className="ri-export-card"
                onClick={() => descargarBlob("/admin/exportar/usuarios.xlsx", "usuarios.xlsx")}
                data-testid="btn-exportar-usuarios"
              >
                <div className="ri-export-icon" style={{ background: "#0F172A" }}>
                  <i className="fa-solid fa-users"/>
                </div>
                <div className="ri-export-info">
                  <div className="ri-export-title">Usuarios (Excel)</div>
                  <div className="ri-export-sub">Sin contraseñas · todos los roles</div>
                </div>
                <i className="fa-solid fa-download ri-export-arrow"/>
              </button>

              <button
                className="ri-export-card"
                onClick={() => descargarBlob("/admin/exportar/backup.zip", "dmu614_backup.zip")}
                data-testid="btn-exportar-zip"
              >
                <div className="ri-export-icon" style={{ background: "#DC2626" }}>
                  <i className="fa-solid fa-file-zipper"/>
                </div>
                <div className="ri-export-info">
                  <div className="ri-export-title">Exportar todo (ZIP)</div>
                  <div className="ri-export-sub">Ambos Excel comprimidos</div>
                </div>
                <i className="fa-solid fa-download ri-export-arrow"/>
              </button>
            </div>
          </section>
        )}

        <section className="text-xs text-slate-400 text-center pb-6 ri-no-print">
          DMU 614 · Sección visible para <span className="mono">admin</span> y <span className="mono">supervisor_general_pipas</span>
          {esAdmin && <> · Exportación de BD restringida a <span className="mono">admin</span></>}.
        </section>
      </main>

      {openImportar && <ModalImportar onClose={() => { setOpenImportar(false); cargar(); }}/>}
    </div>
  );
}
